/*
 * Oppie recept (sections/oppie-recipe.liquid).
 *
 * - Printen: opent de printdialoog. De print-CSS laat alleen het recept staan.
 * - Scherm aan: houdt het scherm wakker tijdens het koken (Screen Wake Lock
 *   API). De schakelaar verschijnt alleen als de browser dat ondersteunt.
 * - Video: opent de receptvideo in een venster (zie setupVideo).
 * Het afvinken van ingredienten is pure CSS en werkt zonder JS.
 */

class RecipePage {
  constructor(root) {
    this.root = root;
    this.lock = null;

    const print = root.querySelector('[data-recipe-print]');
    if (print) print.addEventListener('click', () => window.print());

    this.setupVideo(root);
    this.setupMore(root);

    this.wakeToggle = root.querySelector('[data-recipe-wake]');
    const wakeWrap = root.querySelector('[data-recipe-wake-wrap]');
    if (this.wakeToggle && wakeWrap && 'wakeLock' in navigator) {
      wakeWrap.hidden = false;
      this.wakeToggle.addEventListener('change', () => this.toggleWake());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.wakeToggle.checked) this.requestWake();
      });
    }
  }

  /*
   * Video: de knoppen zijn gewone links naar het videobestand (werkt zonder
   * JS). Met JS opent de video in een <dialog> op schermhoogte en start hij.
   * Sluiten met de knop, Escape of een klik naast de video pauzeert hem.
   */
  setupVideo(root) {
    const dialog = root.querySelector('[data-recipe-video-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const video = dialog.querySelector('video');
    let opener = null;

    root.querySelectorAll('[data-recipe-video-open]').forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        opener = link;
        dialog.showModal();
        if (video) video.play().catch(() => {});
      });
    });

    const close = () => dialog.close();
    const closeButton = dialog.querySelector('[data-recipe-video-close]');
    if (closeButton) closeButton.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      if (video) video.pause();
      if (opener) opener.focus();
    });
  }

  /*
   * "Meer over dit recept" is een <details> die standaard dicht is. Zonder JS
   * of met reduced motion klapt hij gewoon open en dicht. Anders animeert de
   * hoogte rustig met --dur-slow en --ease-out uit de tokens.
   */
  setupMore(root) {
    const details = root.querySelector('[data-recipe-more]');
    const body = details ? details.querySelector('[data-recipe-more-body]') : null;
    const summary = details ? details.querySelector('summary') : null;
    if (!details || !body || !summary || typeof body.animate !== 'function') return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animation = null;

    summary.addEventListener('click', (event) => {
      if (reduceMotion.matches) return;
      event.preventDefault();

      const opening = !details.open || details.dataset.closing === 'true';
      const styles = getComputedStyle(details);
      const duration = parseFloat(styles.getPropertyValue('--dur-slow')) || 360;
      const easing = styles.getPropertyValue('--ease-out').trim() || 'ease-out';
      const startHeight = details.open ? body.getBoundingClientRect().height : 0;

      if (animation) animation.cancel();
      if (opening) {
        delete details.dataset.closing;
        details.open = true;
      } else {
        details.dataset.closing = 'true';
      }
      const endHeight = opening ? body.scrollHeight : 0;

      body.style.overflow = 'hidden';
      animation = body.animate(
        [
          { height: `${startHeight}px`, opacity: opening ? 0 : 1 },
          { height: `${endHeight}px`, opacity: opening ? 1 : 0 },
        ],
        { duration, easing }
      );
      animation.onfinish = () => {
        if (!opening) {
          details.open = false;
          delete details.dataset.closing;
        }
        body.style.overflow = '';
        animation = null;
      };
    });
  }

  async requestWake() {
    try {
      this.lock = await navigator.wakeLock.request('screen');
      this.lock.addEventListener('release', () => {
        this.lock = null;
      });
    } catch (error) {
      this.wakeToggle.checked = false;
    }
  }

  async toggleWake() {
    if (this.wakeToggle.checked) {
      await this.requestWake();
      return;
    }
    if (this.lock) {
      await this.lock.release();
      this.lock = null;
    }
  }
}

document.querySelectorAll('[data-recipe]').forEach((root) => {
  if (!root.dataset.recipeReady) {
    root.dataset.recipeReady = 'true';
    new RecipePage(root);
  }
});
